import { useState, useEffect, useRef, useCallback } from 'react';
import { Logo } from './logo/Logo.tsx';

// 10 minutes in milliseconds
const INACTIVITY_TIMEOUT = 10 * 60 * 1000;

// Animation constants
const LOGO_SIZE = 64;
const SPEED = 2; // pixels per frame
const EDGE_MARGIN = 0; // Margin from screen edges (0 = logo touches edges precisely)

// Throttle delay for high-frequency events (ms)
const THROTTLE_DELAY = 100;

/**
 * Screensaver component that displays a bouncing Consl logo
 * after 10 minutes of user inactivity (DVD-style screensaver).
 * 
 * Performance optimizations:
 * - Uses refs for position/velocity to avoid re-renders during animation
 * - Direct DOM manipulation for smooth 60fps animation
 * - Throttled event listeners for high-frequency events (mousemove)
 * - Hardware-accelerated transforms (transform3d, will-change)
 * - Single requestAnimationFrame loop with proper cleanup
 * 
 * Any user interaction dismisses the screensaver.
 */
const Screensaver = () => {
  const [isActive, setIsActive] = useState(false);
  
  // Use refs for animation state to avoid re-renders during animation
  const positionRef = useRef({ x: 100, y: 100 });
  const velocityRef = useRef({ dx: SPEED, dy: SPEED });
  const logoElementRef = useRef(null);
  
  // Refs for actual logo dimensions (measured from DOM)
  const logoWidthRef = useRef(LOGO_SIZE);
  const logoHeightRef = useRef(LOGO_SIZE);
  
  const timeoutRef = useRef(null);
  const animationRef = useRef(null);
  const isActiveRef = useRef(false); // Track active state for event handlers
  const lastEventTimeRef = useRef(0); // For throttling

  // Keep isActiveRef in sync with isActive state
  useEffect(() => {
    isActiveRef.current = isActive;
  }, [isActive]);

  // Measure actual logo dimensions from DOM
  const measureLogo = useCallback(() => {
    if (logoElementRef.current) {
      const rect = logoElementRef.current.getBoundingClientRect();
      logoWidthRef.current = rect.width;
      logoHeightRef.current = rect.height;
    }
  }, []);

  // Initialize random position and velocity
  const initializePosition = useCallback(() => {
    const maxX = window.innerWidth - logoWidthRef.current - EDGE_MARGIN;
    const maxY = window.innerHeight - logoHeightRef.current - EDGE_MARGIN;
    
    positionRef.current = {
      x: Math.random() * Math.max(EDGE_MARGIN, maxX) + EDGE_MARGIN,
      y: Math.random() * Math.max(EDGE_MARGIN, maxY) + EDGE_MARGIN,
    };
    
    // Randomize initial direction
    const directions = [
      { dx: SPEED, dy: SPEED },
      { dx: -SPEED, dy: SPEED },
      { dx: SPEED, dy: -SPEED },
      { dx: -SPEED, dy: -SPEED },
    ];
    velocityRef.current = directions[Math.floor(Math.random() * directions.length)];
  }, []);

  // Dismiss screensaver and reset timer
  const dismissScreensaver = useCallback(() => {
    if (isActiveRef.current) {
      setIsActive(false);
    }
    
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Start a new timer
    timeoutRef.current = setTimeout(() => {
      initializePosition();
      setIsActive(true);
    }, INACTIVITY_TIMEOUT);
  }, [initializePosition]);

  // Throttled event handler for high-frequency events
  const handleUserActivity = useCallback((event) => {
    const now = Date.now();
    
    // Throttle mousemove and scroll events
    if (event.type === 'mousemove' || event.type === 'scroll') {
      if (now - lastEventTimeRef.current < THROTTLE_DELAY) {
        return;
      }
      lastEventTimeRef.current = now;
    }
    
    dismissScreensaver();
  }, [dismissScreensaver]);

  // Set up event listeners for user activity
  useEffect(() => {
    const events = [
      'mousedown',
      'keydown',
      'scroll',
      'touchstart',
      'wheel',
      'click',
      'focus',
    ];

    // Add all event listeners with passive flag for performance
    events.forEach((event) => {
      document.addEventListener(event, handleUserActivity, { passive: true });
    });

    // Initialize position and start the initial timer
    initializePosition();
    dismissScreensaver();

    return () => {
      // Cleanup event listeners
      events.forEach((event) => {
        document.removeEventListener(event, handleUserActivity);
      });
      
      // Clear timer
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      // Cancel animation
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [handleUserActivity, initializePosition, dismissScreensaver]);

  // Measure logo when screensaver becomes active
  useEffect(() => {
    if (isActive && logoElementRef.current) {
      // Use requestAnimationFrame to ensure DOM is fully rendered
      requestAnimationFrame(() => {
        measureLogo();
        // Adjust position if logo is out of bounds after measurement
        const maxX = window.innerWidth - logoWidthRef.current - EDGE_MARGIN;
        const maxY = window.innerHeight - logoHeightRef.current - EDGE_MARGIN;
        const pos = positionRef.current;
        
        if (pos.x > maxX) pos.x = maxX;
        if (pos.y > maxY) pos.y = maxY;
        if (pos.x < EDGE_MARGIN) pos.x = EDGE_MARGIN;
        if (pos.y < EDGE_MARGIN) pos.y = EDGE_MARGIN;
      });
    }
  }, [isActive, measureLogo]);

  // Handle window resize - re-measure logo and adjust position if needed
  useEffect(() => {
    if (!isActive) return;

    const handleResize = () => {
      measureLogo();
      // Adjust position if logo is out of bounds after resize
      const maxX = window.innerWidth - logoWidthRef.current - EDGE_MARGIN;
      const maxY = window.innerHeight - logoHeightRef.current - EDGE_MARGIN;
      const pos = positionRef.current;
      
      if (pos.x > maxX) pos.x = maxX;
      if (pos.y > maxY) pos.y = maxY;
      if (pos.x < EDGE_MARGIN) pos.x = EDGE_MARGIN;
      if (pos.y < EDGE_MARGIN) pos.y = EDGE_MARGIN;
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isActive, measureLogo]);

  // Animation loop for bouncing logo - uses direct DOM manipulation
  useEffect(() => {
    if (!isActive) {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      return;
    }

    const animate = () => {
      const pos = positionRef.current;
      const vel = velocityRef.current;
      
      // Calculate new position
      let newX = pos.x + vel.dx;
      let newY = pos.y + vel.dy;

      // Get container dimensions using actual logo dimensions
      const maxX = window.innerWidth - logoWidthRef.current - EDGE_MARGIN;
      const maxY = window.innerHeight - logoHeightRef.current - EDGE_MARGIN;

      // Bounce off walls - update velocity ref directly
      if (newX <= EDGE_MARGIN || newX >= maxX) {
        vel.dx = -vel.dx;
        newX = newX <= EDGE_MARGIN ? EDGE_MARGIN : maxX;
      }
      
      if (newY <= EDGE_MARGIN || newY >= maxY) {
        vel.dy = -vel.dy;
        newY = newY <= EDGE_MARGIN ? EDGE_MARGIN : maxY;
      }

      // Update position ref
      pos.x = newX;
      pos.y = newY;

      // Direct DOM update - bypasses React render cycle for smooth animation
      if (logoElementRef.current) {
        logoElementRef.current.style.transform = `translate3d(${newX}px, ${newY}px, 0)`;
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isActive]);

  // Handle click/key to dismiss
  const handleDismiss = useCallback(() => {
    dismissScreensaver();
  }, [dismissScreensaver]);

  if (!isActive) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[9999] cursor-pointer"
      onClick={handleDismiss}
      onKeyDown={handleDismiss}
      role="button"
      tabIndex={-1}
      aria-label="Click to dismiss screensaver"
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.95)',
      }}
    >
      {/* Bouncing Logo - positioned at origin, moved via transform for GPU acceleration */}
      <div
        ref={logoElementRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          // Initial position set via transform - will be updated by animation loop
          transform: `translate3d(${positionRef.current.x}px, ${positionRef.current.y}px, 0)`,
          // Hint to browser for GPU layer optimization
          willChange: 'transform',
          // Ensure crisp rendering
          backfaceVisibility: 'hidden',
        }}
      >
        <Logo 
          variant="wordmark" 
          size={LOGO_SIZE}
          text="Consl"
        />
      </div>
    </div>
  );
};

export default Screensaver;
